<template>
  <div
    v-if="showBanner"
    class="banner w-100 d-flex text-center justify-content-center"
    :style="{ backgroundColor }"
  >
    <span class="d-flex justify-content-center align-items-center flex-grow-1">
      <span
        v-if="currentIconSvg"
        class="banner-icon-svg mr-2"
        :style="{ color: iconColor, '--icon-color': iconColor || undefined }"
        v-html="currentIconSvg"
      />
      <span
        class="banner-text d-flex align-items-center"
        :style="{ color: textColor }"
      >
        <span v-html="text"></span>
        <a
          v-if="buttonText"
          :href="buttonLink"
          :title="buttonTitlePlain"
          target="_blank"
          rel="noopener"
          class="banner-link btn font-weight-bold"
          :style="{
            color: buttonTextColor,
            backgroundColor: buttonBackgroundColor,
            borderColor: buttonBackgroundColor
          }"
          @click="sendLog"
        >
          {{ buttonText }}
        </a>
      </span>
    </span>
    <button
      @click="closeBanner"
      class="close mr-3 bg-transparent border-0"
      aria-label="Fechar aviso"
    >
      &times;
    </button>
  </div>
</template>

<script>
import bannerNotificationService from '@/services/banner-notification'
import { bannerIconPathmap, defaultBannerIconName } from '@/assets/icons/banner-icons'

const packageConfig = require('../../package.json')

export default {
  name: 'TheBanner',
  data () {
    return {
      showBanner: false,
      datadogLogsBanner: null,
      messageLog: null,
      text: null,
      textColor: null,
      buttonText: null,
      buttonLink: null,
      buttonTextColor: null,
      buttonTitle: null,
      buttonBackgroundColor: null,
      backgroundColor: null,
      iconColor: null,
      iconName: null
    }
  },
  computed: {
    currentIconSvg () {
      if (!this.iconName) return null
      const exact = bannerIconPathmap[this.iconName]
      if (exact) return exact
      const lower = this.iconName.toLowerCase()
      const key = Object.keys(bannerIconPathmap).find(k => k.toLowerCase() === lower)
      return bannerIconPathmap[key] || null
    },
    /** Texto puro para o tooltip (title não suporta HTML) */
    buttonTitlePlain () {
      if (!this.buttonTitle) return ''
      const div = document.createElement('div')
      div.innerHTML = this.buttonTitle
      return (div.textContent || div.innerText || '').trim()
    }
  },
  methods: {
    async getActiveBannerNotification () {
      const result = await bannerNotificationService.getActiveNotification()

      if (result.success) {
        const { data } = result
        console.log('banner notification data (checar nome do campo do ícone):', data)
        this.messageLog = data.messageLog
        this.text = data.text
        this.textColor = data.textColor
        this.buttonText = data.buttonText
        this.buttonLink = data.utmPortal ? `${data.buttonLink}${data.utmPortal}` : data.buttonLink
        this.buttonTextColor = data.buttonTextColor
        this.buttonTitle = data.buttonTitle
        this.buttonBackgroundColor = data.buttonBackgroundColor
        this.backgroundColor = data.backgroundColor
        this.iconColor = data.iconColor
        this.iconName = data.iconName
        this.showBanner = bannerNotificationService.shouldShowBanner()
      } else {
        this.showBanner = false
      }
    },
    sendLog () {
      if (this.datadogLogsBanner) {
        if (this.$store && this.$store.state && this.$store.state.auth && this.$store.state.auth.user) {
          this.datadogLogsBanner.info(this.messageLog, {
            email: this.$store.state.auth.user.email,
            domain: this.$store.state.auth.organization.domain,
            role: this.$store.state.auth.user.role
          })
        }
      }
      this.closeBanner()
    },
    closeBanner () {
      this.showBanner = false
      bannerNotificationService.closeBanner()
    },
    updateBannerHeight (height) {
      document.documentElement.style.setProperty('--banner-height', `${height}px`)
    },
    setupResizeObserver () {
      if (this.resizeObserver) return
      this.resizeObserver = new ResizeObserver(entries => {
        for (const entry of entries) {
          if (this.showBanner) {
            this.updateBannerHeight(entry.contentRect.height)
          } else {
            this.updateBannerHeight(0)
          }
        }
      })
      this.resizeObserver.observe(this.$el)
    }
  },
  beforeMount () {
    if (window.DD_LOGS) {
      if (window.DD_LOGS_INITIALIZED) return
      window.DD_LOGS.init({
        clientToken: process.env.DATADOG_CLIENT_TOKEN,
        site: process.env.DATADOG_SITE,
        service: process.env.DATADOG_SERVICE,
        env: process.env.NODE_ENV,
        version: packageConfig.version,
        forwardErrorsToLogs: false,
        sessionSampleRate: 100
      })
      window.DD_LOGS_INITIALIZED = true
    }
  },
  mounted () {
    this.$nextTick(() => {
      this.getActiveBannerNotification()
      if (window.DD_LOGS && window.DD_LOGS_INITIALIZED) {
        this.datadogLogsBanner = window.DD_LOGS.createLogger({
          level: 'info',
          handler: 'http',
          context: { logSource: process.env.DATADOG_LOG_SOURCE }
        })
      }
      this.setupResizeObserver()
    })
  },
  beforeDestroy () {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }
    this.updateBannerHeight(0)
  },
  watch: {
    showBanner (val) {
      this.$nextTick(() => {
        this.updateBannerHeight(val ? this.$el.offsetHeight : 0)
      })
    }
  }
}
</script>

<style scoped>
.banner {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08) !important;
  z-index: 99999 !important;
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  width: 100% !important;
  padding: 0.35rem 1rem !important;
}
.banner-text {
  font-family: Ubuntu, Arial, sans-serif;
  font-size: 0.9375rem !important;
  font-weight: 400;
}
.banner-link {
  font-family: Ubuntu, Arial, sans-serif;
  text-align: center !important;
  place-content: center !important;
  font-size: 0.8125rem !important;
  font-weight: 600 !important;
  border-radius: 0.25rem !important;
  height: 28px !important;
  padding: 0.25rem 0.5rem !important;
  margin-left: 0.75rem !important;
  border: none !important;
}
.banner-icon-svg {
  display: inline-flex;
  transform: translateY(1px);
  width: 1rem;
  height: 1rem;
  min-width: 1rem;
  min-height: 1rem;
}
.banner-icon-svg svg {
  width: 20px;
  height: 20px;
  display: block;
}
.banner .close {
  font-size: 1.15rem !important;
  cursor: pointer !important;
  color: #FFFFFF !important;
}
</style>



// WEBPACK FOOTER //
// src/components/Banner.vue